import { toError } from "@clipboard-health/util-ts";
import { Stack, useTheme } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import {
  APP_V2_APP_EVENTS,
  APP_V2_USER_EVENTS,
  logError,
  logEvent,
} from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useIsKeyboardOpen } from "@src/appV2/lib/utils/capacitor/useIsKeyboardOpen";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";
import { type ReactElement } from "react";

import { AddRolesErrorState } from "../../../Roles/AddRoles/ErrorState";
import { AddRolesFooter } from "../../../Roles/AddRoles/Footer";
import { AddRolesLoadingState } from "../../../Roles/AddRoles/LoadingState";
import { AddRolePageContent } from "../../../Roles/AddRoles/PageContent";
import { useAddRolesData } from "../../../Roles/AddRoles/useAddRolesData";
import { useCreateShoppableQualification } from "../../../Roles/api/useCreateShoppableQualification";
import { useDeleteShoppableQualification } from "../../../Roles/api/useDeleteShoppableQualification";
import { useUpdateOnboardingWorker } from "../../api/useUpdateOnboardingWorker";
import { OnboardingTitle } from "../../SharedTitle";
import { type OnboardingStepProps } from "../OnboardingStepWrapper";

export function RoleStepContent(props: OnboardingStepProps): ReactElement {
  const { onNext, stage } = props;

  const {
    allQualifications,
    qualificationsByCategories,
    qualificationsAreLoading,
    qualificationsAreLoaded,
    qualificationsAreError,
    autoApprovedQualifications,
    workerId,
    workerQualificationIds,
    scrollContainerRef,
    shoppableQualifications,
    qualificationNameToCredentialMap,
  } = useAddRolesData();

  const { showErrorToast } = useToast();
  const theme = useTheme();

  const isKeyboardOpen = useIsKeyboardOpen({ keyboardOpenEvent: "keyboardWillShow" });

  const { mutateAsync: createShoppableQualification, isLoading: isCreatingShoppableQualification } =
    useCreateShoppableQualification();

  const { mutateAsync: updateOnboardingWorker, isLoading: isUpdatingOnboardingWorker } =
    useUpdateOnboardingWorker();

  const { mutateAsync: deleteShoppableQualification, isLoading: isDeletingShoppableQualification } =
    useDeleteShoppableQualification();

  useLogEffect(APP_V2_USER_EVENTS.ONBOARDING_ADD_ROLES_PAGE_VIEWED);

  if (qualificationsAreError) {
    // by default, the error page is set to gradient, but we need to set it to tertiary for the onboarding flow
    return <AddRolesErrorState sx={{ background: theme.palette.background.tertiary }} />;
  }

  return (
    <>
      <PageContent>
        <OnboardingTitle text="What type of roles are you interested in?" />

        <Stack sx={{ marginTop: 6 }}>
          {qualificationsAreLoading && <AddRolesLoadingState />}

          {qualificationsAreLoaded && (
            <AddRolePageContent
              ref={scrollContainerRef}
              qualificationsByCategories={qualificationsByCategories}
              autoApprovedQualifications={autoApprovedQualifications}
              qualificationNameToCredentialMap={qualificationNameToCredentialMap}
              workerQualificationIds={workerQualificationIds}
              onCreateShoppableQualification={async (qualificationId) => {
                const qualification = allQualifications.find(
                  (qualification) => qualification.id === qualificationId
                );

                if (!qualification) {
                  return;
                }

                try {
                  await createShoppableQualification({
                    workerId,
                    qualificationId,
                  });

                  logEvent(APP_V2_USER_EVENTS.ROLE_ADDED, {
                    qualificationId,
                    workerId,
                    isOnboarding: true,
                    screen: "SIGNUP",
                  });
                } catch (maybeError) {
                  const error = toError(maybeError);

                  logError(APP_V2_APP_EVENTS.ADD_ROLE_FAILURE, {
                    metadata: {
                      qualificationId,
                      workerId,
                      isOnboarding: true,
                      screen: "SIGNUP",
                    },
                    error,
                  });
                  throw error;
                }
              }}
            />
          )}
        </Stack>
      </PageContent>
      <AddRolesFooter
        addedQualificationIds={workerQualificationIds}
        qualifications={allQualifications}
        shoppableQualifications={shoppableQualifications}
        isVisible={isKeyboardOpen}
        isLoadingContinueButton={isUpdatingOnboardingWorker}
        canContinue={
          workerQualificationIds.length > 0 &&
          !isCreatingShoppableQualification &&
          !isDeletingShoppableQualification
        }
        onDeleteRole={async (shoppableQualificationId, qualificationId) => {
          await deleteShoppableQualification({
            workerId,
            shoppableQualificationId,
          });

          logEvent(APP_V2_USER_EVENTS.ROLE_REMOVED, {
            qualificationId,
            workerId,
            isOnboarding: true,
            screen: "SIGNUP",
          });
        }}
        onDeleteRoleError={async (qualificationId, error) => {
          showErrorToast("Something went wrong while removing the role. Please try again.");

          logError(APP_V2_APP_EVENTS.REMOVE_ROLE_FAILURE, {
            metadata: {
              qualificationId,
              workerId,
              isOnboarding: true,
              screen: "SIGNUP",
            },
            error,
          });
        }}
        onDone={async () => {
          await updateOnboardingWorker({
            stage: "updateStageOnly",
            newStage: stage,
            signupFlowVersion: "V2",
          });

          logEvent(APP_V2_USER_EVENTS.ONBOARDING_STEP_COMPLETED, {
            stepCompleted: stage,
          });

          void onNext();
        }}
      />
    </>
  );
}
